import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { endpointTestsApi } from '@/services/api';
import { trackEvent } from '@/lib/analytics';
import { saveBlobAsFile } from '@/utils/downloadFile';
import { exportCsvToJson } from '@/components/endpoints/tests/importMapping';
import type {
  CreateEndpointTestInput,
  EndpointTestExportFormat,
  ImportEndpointTestsInput,
  UpdateEndpointTestInput,
} from '@/types/endpointTests';

/**
 * React Query hooks for saved endpoint tests (task 365). List rows carry a
 * lastRun summary; full run history is fetched lazily per test (the
 * EndpointRequestHistory expanded-row precedent).
 */

/** The saved tests for one endpoint. */
export const useEndpointTests = (endpointId: string | undefined) => {
  return useQuery({
    queryKey: ['endpoint-tests', endpointId],
    queryFn: () => endpointTestsApi.list(endpointId!).then(res => res.data),
    enabled: !!endpointId,
  });
};

/** Run history for one test — lazy, enabled only when the row is expanded. */
export const useEndpointTestRuns = (testId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ['endpoint-test-runs', testId],
    queryFn: () => endpointTestsApi.listRuns(testId!).then(res => res.data),
    enabled: enabled && !!testId,
  });
};

export const useCreateEndpointTest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEndpointTestInput) =>
      endpointTestsApi.create(data).then(res => res.data.data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-tests', created.endpointId] });
    },
  });
};

export const useUpdateEndpointTest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEndpointTestInput }) =>
      endpointTestsApi.update(id, data).then(res => res.data.data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-tests', updated.endpointId] });
    },
  });
};

export const useDeleteEndpointTest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // DELETE returns the deleted test, so the endpointId for invalidation
    // rides back on the response.
    mutationFn: (id: string) =>
      endpointTestsApi.delete(id).then(res => res.data.data),
    onSuccess: (deleted) => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-tests', deleted.endpointId] });
    },
  });
};

export const useRunEndpointTest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    // endpointId is passed alongside the test id so the list (lastRun summary)
    // can be invalidated — the run result only carries endpointTestId.
    mutationFn: ({ id }: { id: string; endpointId: string }) =>
      endpointTestsApi.run(id).then(res => res.data.data),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-tests', variables.endpointId] });
      queryClient.invalidateQueries({ queryKey: ['endpoint-test-runs', variables.id] });
      // `endpoint_tested` (task 427) — the saved-test half of testing. The call
      // resolving does not mean the run passed, so success reflects the
      // recorded run status, not the HTTP outcome.
      trackEvent('endpoint_tested', {
        endpoint_id: variables.endpointId,
        success: result?.run?.status === 'completed',
        test_kind: 'saved',
      });
    },
    onError: (_error, variables) => {
      trackEvent('endpoint_tested', {
        endpoint_id: variables.endpointId,
        success: false,
        test_kind: 'saved',
      });
    },
  });
};

/** Read a blob as text (FileReader: every target browser and jsdom have it). */
function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error('The export could not be read.'));
    reader.readAsText(blob);
  });
}

/**
 * Bulk import (task 1175): the rows the browser parsed plus the column map.
 * Answers `{ created, skipped }`; the list is invalidated so the new
 * `imported` rows appear.
 */
export const useImportEndpointTests = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ImportEndpointTestsInput) =>
      endpointTestsApi.import(data).then(res => res.data.data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['endpoint-tests', variables.endpointId] });
    },
  });
};

/**
 * Export the tests list as CSV or JSON (task 1175) and hand it to the browser.
 * A mutation rather than a query: it is a one-shot User action with a pending
 * state, and nothing should cache the bytes.
 *
 * Both formats read the service's CSV export (`format=csv`, every test, the
 * import column shape). The service's `format=json` is the ordinary paginated
 * list, so the JSON file is built here from the CSV rows — an array that the
 * Import dialog accepts back unchanged.
 */
export const useExportEndpointTests = () =>
  useMutation({
    mutationFn: async ({
      endpointId,
      format,
      filename,
    }: {
      endpointId: string;
      format: EndpointTestExportFormat;
      filename: string;
    }) => {
      const res = await endpointTestsApi.exportFile(endpointId, 'csv');
      if (format === 'csv') {
        saveBlobAsFile(res.data, `${filename}.csv`);
        return;
      }
      const rows = exportCsvToJson(await blobText(res.data));
      saveBlobAsFile(
        new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' }),
        `${filename}.json`,
      );
    },
  });
